// cohorts/create services
// Pure async functions only: no React, no hooks, no state. Parse at this boundary.
import { privateApi } from "@core/libs/axios";
import { parseWith } from "@core/parse";
import { CohortSchema, type Cohort } from "../../_shared/types";
import {
  AdministratorOptionsSchema,
  type AdministratorOption,
  type CreateCohortFormValues,
} from "../types";

/**
 * `POST /cohorts/`: create a cohort (President only). The server generates the code, queues the
 * email to the responsible Administrator, and answers `201` with the cohort.
 *
 * Needs an `Idempotency-Key`: a retry with the same key and the same payload returns the original
 * cohort instead of creating a second one. The caller owns the key (see `useIdempotencyKey`).
 * Failures are left as `ApiError`; `classifyCreateFailure` reads them.
 */
export async function createCohort(
  values: CreateCohortFormValues,
  idempotencyKey: string
): Promise<Cohort> {
  const dto = await privateApi
    .post<unknown>(
      "/cohorts/",
      {
        name: values.name.trim(),
        places: values.places,
        waitlist_places: values.waitlistPlaces,
        program_start_date: values.startDate,
        program_end_date: values.endDate,
        responsible_administrator_id: values.administratorId,
      },
      { headers: { "Idempotency-Key": idempotencyKey } }
    )
    .then((r) => r.data);
  return parseWith(CohortSchema, dto, "POST /cohorts/");
}

/**
 * The Administrators the President can name as responsible: the active ones of the team list
 * (`GET /auth/team/`). The cohort API checks the choice again.
 */
export async function listAdministrators(): Promise<AdministratorOption[]> {
  const dto = await privateApi.get<unknown>("/auth/team/").then((r) => r.data);
  const team = parseWith(AdministratorOptionsSchema, dto, "GET /auth/team/");
  return team.members.filter(
    (member) =>
      member.role === "administrator" && member.account_status === "ACTIVE"
  );
}
